import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler } from 'react-hook-form';

import { Box, Tab, TabGroup } from '@base-app';
import { WITHDRAWAL_TRANSLATION } from '../constants/common';
import { PageLarge, Section, SubPage } from '@builder';
import { Sizes } from '@/common/types';

import { FormWithdrawalResponse, TabId } from './WithdrawalScreen.types';
import { NotificationVariants, TabContextProvider } from '@/common/components';

import WithdrawalForm from '../components/WithdrawalForm/WithdrawalForm';
import SuccessPage from '../components/SuccessPage/SuccessPage';

import { WithdrawalFormValues } from '../components/WithdrawalForm/WithdrawalForm.types';
import useMutationSubmitWithdrawalForm from '../hooks/useMutationSubmitWithdrawalForm';
import { useAddNotification } from '@/modules/notifications/store';

const WithdrawalScreen: FC = () => {
  const { t } = useTranslation(WITHDRAWAL_TRANSLATION);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [withdrawalRequestId, setWithdrawalRequestId] = useState<string>('');
  const addNotification = useAddNotification();

  const handlerSuccess = async ({ requestId }: FormWithdrawalResponse): Promise<void> => {
    setWithdrawalRequestId(requestId);
  };

  const handlerError = (error: any): void => {
    addNotification({
      title: error.response.data,
      type: NotificationVariants.ERROR,
      autoClose: false,
    });
  };

  const { mutate, isLoading } = useMutationSubmitWithdrawalForm(handlerSuccess, handlerError);

  const resetForm = (): void => {
    setWithdrawalRequestId('');
    setIsSubmitted(false);
  };

  const handleFormSubmit: SubmitHandler<WithdrawalFormValues> = (values): void => {
    mutate(values);
  };

  useEffect(() => {
    if (withdrawalRequestId !== '') {
      setIsSubmitted(true);
    }
  }, [withdrawalRequestId]);

  return (
    <TabContextProvider value={TabId.WITHDRAWAL}>
      <PageLarge
        header={
          <Box
            css={{
              boxShadow: '$tabGroup',
              marginTop: '$4',
            }}
          >
            <TabGroup
              css={{
                borderBottom: 'none',
                margin: '$1 auto',
                maxWidth: '$studyInsights$containerMaxWidth',
                padding: '0 $6',
              }}
              value={TabId.WITHDRAWAL}
              tabSize={Sizes.LARGE}
            >
              <Tab label={t('titles.tab_title')} value={TabId.WITHDRAWAL} />
            </TabGroup>
          </Box>
        }
        title={t('titles.page_title')}
      >
        <SubPage>
          <Section>
            {isSubmitted ? (
              <SuccessPage handleResetForm={resetForm} requestId={withdrawalRequestId} />
            ) : (
              <WithdrawalForm handleFormSubmit={handleFormSubmit} isLoading={isLoading} />
            )}
          </Section>
        </SubPage>
      </PageLarge>
    </TabContextProvider>
  );
};

export default WithdrawalScreen;
